import { Component, computed, inject, signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { SolutionOption as SolutionOptionModel } from '../../../../core/models/solution.model';
import { AuthService } from '../../../../core/services/auth.service';
import { SolutionsService } from '../../../../core/services/solutions.service';
import { toSolutionOptions } from '../../../../core/utils/solution-catalog.util';
import { Modalbase } from '../modalbase';
import { SolutionOption } from '../../solution-option/solution-option';

/**
 * Cambio de solución desde el ítem "Soluciones" del navbar.
 *
 * Carga las soluciones habilitadas en el workspace activo y cierra devolviendo la elegida,
 * o `undefined` si el usuario cancela; quien lo abre decide a dónde lleva.
 */
@Component({
  selector: 'app-switch-solution-modal',
  standalone: true,
  imports: [TranslateModule, MatProgressSpinnerModule, Modalbase, SolutionOption],
  templateUrl: './switch-solution-modal.html',
  styleUrl: './switch-solution-modal.scss',
})
export class SwitchSolutionModal {
  private dialogRef = inject(MatDialogRef<SwitchSolutionModal>);
  private solutionsService = inject(SolutionsService);
  private authService = inject(AuthService);

  protected solutions = signal<SolutionOptionModel[]>([]);
  protected isLoading = signal<boolean>(true);
  protected hasError = signal<boolean>(false);
  protected selectedKey = signal<string | null>(null);

  protected canContinue = computed<boolean>(() => !!this.selectedKey());

  constructor() {
    this.loadSolutions();
  }

  private loadSolutions(): void {
    const workspaceId = this.authService.getWorkspaceId();
    if (!workspaceId) {
      this.isLoading.set(false);
      this.hasError.set(true);
      return;
    }

    this.solutionsService.getByWorkspaceId(workspaceId).subscribe({
      next: (workspaceSolutions) => {
        this.solutions.set(toSolutionOptions(workspaceSolutions));
        this.isLoading.set(false);
      },
      error: (error: unknown) => {
        console.error('Unable to load the workspace solutions', error);
        this.isLoading.set(false);
        this.hasError.set(true);
      },
    });
  }

  protected isSelected(solution: SolutionOptionModel): boolean {
    return solution.key === this.selectedKey();
  }

  protected onSelectSolution(solution: SolutionOptionModel): void {
    this.selectedKey.set(solution.key);
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected onContinue(): void {
    const selected = this.solutions().find((solution) => solution.key === this.selectedKey());
    this.dialogRef.close(selected ?? undefined);
  }
}
